import Dinero from 'dinero.js'
import { get } from 'lodash'
import {
  ADJUSTMENT_TYPES,
  UNIT_IDS,
  MEASURE_UNIT,
  DEFAULT_SUPPLIER_TYPE_VENDOR_CLASS_IDS,
  LOAD_BOARD_ITEM_DATE_ICONS,
  ROUNDING_PRECISIONS,
} from 'utils/constants'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { convertUM, locationIsNotEmpty } from 'utils/helpers'
import Big from 'big.js'

export const generateIdFromCode = code => `${code}-${Math.floor(Math.random() * 1000) + 1}`

export const toSnailCase = str => str.replace(/\s/g, '_').toLowerCase()

const getTotalDeductionForType = (deductionType, deductions = [], originalValue = 0) =>
  deductions.reduce((acc, deduction) => {
    let deductionAmount = 0
    if (deduction.type === deductionType) {
      if (deduction.unit === UNIT_IDS.PERCENTAGE) {
        deductionAmount = (Number(originalValue) * Number(deduction.amount || 0)) / 100
      } else {
        deductionAmount = Number(deduction.amount || 0)
      }
    }
    return roundTo(acc + deductionAmount, ROUNDING_PRECISIONS.PRICE)
  }, 0)

export const getTotalWeightDeduction = (deductions = [], originalValue = 0) =>
  roundTo(
    getTotalDeductionForType(ADJUSTMENT_TYPES.WEIGHT, deductions, Number(originalValue)),
    ROUNDING_PRECISIONS.WEIGHT,
  )

export const getTotalPriceDeduction = (deductions = [], originalValue = 0) =>
  roundTo(getTotalDeductionForType(ADJUSTMENT_TYPES.COST, deductions, Number(originalValue)), ROUNDING_PRECISIONS.COST)

export const generateAdjustmentText = (weightDeductions, priceDeductions, pricePrepend = '$') => {
  // convert weight deduction into either 2 (when true) or 0 (when false)
  // convert price deduction into ether 1 (when true) or 0 (when false)
  // when summed, this generates 4 unique possible results
  switch (Number(!!weightDeductions) * 2 + Number(!!priceDeductions)) {
    case 3:
      return 'Mixed'
    case 2:
      return weightDeductions?.toString()
    case 1:
      return `${pricePrepend}${priceDeductions.toFixed(2)}`
    default:
      return ''
  }
}

export const generateAdjustmentTextFromMaterial = (material, pricePrepend = '$', defaultYardUm) => {
  const { gross, tare, netPrice, deductions = [], um } = material?.weightAndPrice || {}
  const formattedGross = parseFloat(gross?.amount || 0)
  const formattedTare = parseFloat(tare?.amount || 0)
  const formattedPrice = Dinero({ ...netPrice }).toUnit()

  const totalWeightDeduction = getTotalWeightDeduction(deductions, formattedGross - formattedTare)
  const totalPriceDeduction = getTotalPriceDeduction(
    deductions,
    convertUM((formattedGross - formattedTare) * formattedPrice, defaultYardUm, um),
  )
  const deductionText = generateAdjustmentText(totalWeightDeduction, totalPriceDeduction, pricePrepend)

  return deductionText || '---'
}

export const getNetWeight = ({ gross = 0, tare = 0, deductions }) =>
  (Number(gross * 10) - Number(tare * 10) - getTotalWeightDeduction(deductions, Number(gross) - Number(tare)) * 10) / 10

export const getNetValue = ({ gross = 0, tare = 0, price = 0, deductions, fromUnit, toUnit }) => {
  // The logic here is weird, as price and weight may not be in the same unit of measure. It somehow ends up working out
  // after the conversion on the next line, but it is not intuitive at all.
  const initialValue = parseFloat(getNetWeight({ gross, tare, deductions }) * Number(price * 10000)) / 10000

  const trueValue = convertUM(initialValue, fromUnit, toUnit)

  // Values are payable amounts, so they must be rounded to the nearest cent
  return roundTo(trueValue - getTotalPriceDeduction(deductions, trueValue), ROUNDING_PRECISIONS.COST)
}

export const calculateTotalWeight = (materials, settings = {}) => {
  const { fieldName = 'targetWeight' } = settings
  return materials.reduce((aggregate, material) => {
    const targetWeight = material[fieldName] || 0
    return aggregate + parseInt(targetWeight, 10)
  }, 0)
}

export const calculateTotalValue = (materials, settings = {}) => {
  const { weightFieldName = 'targetWeight', pricePath = 'price' } = settings
  return materials.reduce((aggregate, material) => {
    const targetWeight = material?.[weightFieldName] || 0
    const rawPrice = get(material, pricePath)
    const price = parseFloat(typeof rawPrice === 'object' ? Dinero(rawPrice).toUnit() : rawPrice) || 0
    return aggregate + roundTo(targetWeight * price, ROUNDING_PRECISIONS.COST)
  }, 0)
}

/** Converts price / fromUnit to price / toUnit. DO NOT USE THIS FUNCTION TO CONVERT MASS (WEIGHT) */
export const convertPriceWeightUnits = (price, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return new Big(price)

  // Prices are defined as value / mass. This means we need to invert the conversion factor for this use case
  return new Big(1)
    .div(convertUM(1, fromUnit, toUnit, true))
    .mul(new Big(price))
    .round(ROUNDING_PRECISIONS.INTERNAL, Big.roundHalfUp)
}

/** Converts an amount of mass(weight) in fromUnit to toUnit. DO NOT USE THIS FUNCTION TO CONVERT PRICES */
export const convertWeightUnits = (weight, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return new Big(weight)
  return new Big(convertUM(weight, fromUnit, toUnit, true)).round(ROUNDING_PRECISIONS.INTERNAL, Big.roundHalfUp)
}

/**
 * Takes a string representation of a date or a Date object and adds or subtracts days.
 * @param {string|Date} dateInput An ISO date string or a Date object to which you want to add or subtract days.
 * @param {number} [dateOffset=1] The number of days you want to add or subtract.
 * @return {Date} A new JavaScript Date object.
 */
export const addToDate = (dateInput, dateOffset = 1) => {
  const newDate = new Date(dateInput)
  newDate.setDate(newDate.getDate() + dateOffset)
  return newDate
}

/**
 * Takes a string representation of a date or a Date object and adds or subtracts milliseconds.
 * @param {string|Date} dateInput An ISO date string or a Date object to which you want to add or subtract milliseconds.
 * @param {number} [dateOffset=1] The number of milliseconds you want to add or subtract.
 * @return {Date} A new JavaScript Date object.
 */
export const addToTime = (dateInput, timeOffset = 1000) => {
  const newDate = new Date(dateInput)
  newDate.setTime(newDate.getTime() + timeOffset)
  return newDate
}

const getAddressFromContact = contact => ({
  name: [contact?.firstName, contact?.lastName].join(' '),
  address1: contact?.address1,
  address2: contact?.address2,
  city: contact?.city,
  state: contact?.state,
  zip: contact?.zip,
})

export const getContactAddress = customerData => {
  const primaryContact = customerData?.contacts?.find(contact => !!contact.isPrimaryContact)
  if (primaryContact?.address1) {
    return getAddressFromContact(primaryContact)
  }
  const firstContact = customerData?.contacts?.find(contact => !!contact.address1)
  if (firstContact) {
    return getAddressFromContact(firstContact)
  }
  const validCustomerLocations = customerData?.locations?.filter(location => locationIsNotEmpty(location)) || []
  const firstLocation = validCustomerLocations[0]
  if (firstLocation) {
    return firstLocation
  }
  return {}
}

export const formatScrapVehicles = (scrapVehicles = []) => {
  const formattedScrapVehicles = []
  const scrapVehiclesAsMaterials = []
  scrapVehicles.forEach(scrapVehicle => {
    const { id: scrapVehicleId, rowIndex, year, color, make, model } = scrapVehicle
    const code = `${year} ${color} ${make} ${model}`.replace(/\s\s+/g, ' ').trim()
    scrapVehiclesAsMaterials.push({
      id: scrapVehicleId,
      rowIndex,
      rowType: 'scrap-vehicle',
      materialCode: 'scrap-vehicle',
      code,
      materialName: code,
      vin: scrapVehicle.vin,
    })
    formattedScrapVehicles.push({
      ...scrapVehicle,
      rowId: scrapVehicleId,
    })
  })
  return { scrapVehiclesAsMaterials, formattedScrapVehicles }
}

export const formatVehicleLabel = vehicle => {
  let blankSpace = ''
  if (vehicle?.make && vehicle?.model) blankSpace = ' '
  const makeAndModel = vehicle?.make || vehicle?.model ? ` (${vehicle?.make}${blankSpace}${vehicle?.model})` : ''
  return `${vehicle?.plateNumber}${makeAndModel}`
}

export const formatVehicle = vehicle => ({
  ...(vehicle || {}),
  id: vehicle?.id,
  label: formatVehicleLabel(vehicle),
  customerId: vehicle?.customerId,
  plateNumber: vehicle?.plateNumber,
})

export const getCustomerVehicles = (vehicles = []) => {
  if (vehicles?.length === 0) return []
  return vehicles?.map(formatVehicle)
}

export const sortSupplierTypeVendorClass = (a, b) => {
  const aDefaultIndex = DEFAULT_SUPPLIER_TYPE_VENDOR_CLASS_IDS.indexOf(a.id)
  const bDefaultIndex = DEFAULT_SUPPLIER_TYPE_VENDOR_CLASS_IDS.indexOf(b.id)
  if (aDefaultIndex > -1 && bDefaultIndex === -1) return -1
  if (aDefaultIndex === -1 && bDefaultIndex > -1) return 1
  if (aDefaultIndex > -1 && bDefaultIndex > -1) return aDefaultIndex - bDefaultIndex
  if (a?.name?.toLowerCase() < b?.name?.toLowerCase()) return -1
  if (a?.name?.toLowerCase() > b?.name?.toLowerCase()) return 1
  return 0
}

export const sortByName = (a, b) => {
  if (a.name.toLowerCase() < b.name.toLowerCase()) return -1
  if (a.name.toLowerCase() > b.name.toLowerCase()) return 1
  return 0
}

/**
 * This capitalizes all words in a string for display purposes
 */
export const capAllWords = string => {
  const words = string.split(' ')

  const capitalizedWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1))

  return capitalizedWords.join(' ')
}

/**
 * This is currently not being used. It will be used when the load board includes
 * tickets with past scheduledDate's
 *
 * This is a function that takes in a date and determines if it is today, in the future, or past
 * and returns the appropriate alert icon
 */
export const determineDateContextIcon = (date, status) => {
  if (status === 'COMPLETED' || status === 'VOID') {
    return LOAD_BOARD_ITEM_DATE_ICONS.TODAY
  }

  const currentDate = new Date()
  currentDate.setHours(0, 0, 0, 0)

  const yesterdayDate = new Date(currentDate)
  yesterdayDate.setDate(currentDate.getDate() - 1)
  yesterdayDate.setHours(0, 0, 0, 0)

  const inputDate = new Date(date)
  inputDate.setHours(0, 0, 0, 0)

  if (inputDate.getTime() >= currentDate.getTime()) {
    return LOAD_BOARD_ITEM_DATE_ICONS.TODAY
  } else if (inputDate.getTime() === yesterdayDate.getTime()) {
    return LOAD_BOARD_ITEM_DATE_ICONS.YESTERDAY
  } else {
    return LOAD_BOARD_ITEM_DATE_ICONS.OLDER
  }
}

/**
 * Takes in an object from the yardsDetailsList and returns the object formatted for an asset blade dropdown
 */
export const objectDetailsToDropdownFormatter = (yardDetailObject, index, options = {}) => {
  const { idKey = 'id', nameKey = 'name' } = options
  return {
    id: yardDetailObject[idKey],
    name: capAllWords(yardDetailObject[nameKey]),
    spacedName: capAllWords(yardDetailObject[nameKey]),
    index,
  }
}
